import ShotBatchExample from '../../../curriculum/components/ShotBatchExample';
import React, { useEffect, useState } from 'react';
import type { CurriculumResource, LevelLite } from '../../../curriculum/adapters/types';
import { extractPdfPoster } from '../../classroom/pdfPoster';
import {
  ResolvedSessionPlan,
  formatSlotDate,
} from '../../utils/sessionPlan';
import { PortfolioEntry, summarizeEntriesForLevel } from '../../classroom/types';
import { videoEmbedUrl } from '../../classroom/aiCreation';

interface ClassesViewProps {
  plan: ResolvedSessionPlan;
  levels: LevelLite[];
  entries: PortfolioEntry[];
}

const mediaMatches = (resource: CurriculumResource | undefined, pattern: RegExp) =>
  [resource?.title, resource?.url].some((value) => pattern.test(String(value || '').split(/[?#]/)[0]));
const isImage = (resource?: CurriculumResource) => mediaMatches(resource, /\.(avif|gif|jpe?g|png|webp)$/i);
const isPdf = (resource?: CurriculumResource) => mediaMatches(resource, /\.pdf$/i);
const isVideo = (resource?: CurriculumResource) => mediaMatches(resource, /\.(mov|m4v|mp4|webm)$/i);
const isAudio = (resource?: CurriculumResource) => resource?.kind === 'audio'
  || mediaMatches(resource, /\.(aac|aif|aiff|alac|flac|m4a|mp3|oga|ogg|opus|wav|weba|wma)$/i);
const exampleLabel = (resource: CurriculumResource | undefined, fallback: string) =>
  resource?.description || resource?.title.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ') || fallback;

const LinkIndicator: React.FC = () => (
  <span className="cd-example-link-indicator">
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 14a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 0 0-5-5l-1.25 1.25" />
      <path d="M14 10a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 0 0 5 5l1.25-1.25" />
    </svg>
    <span>Open link</span>
    <span aria-hidden="true">↗</span>
  </span>
);

const ExampleMedia: React.FC<{ resource?: CurriculumResource; variant: 'artifact' | 'ai'; large?: boolean }> = ({ resource, variant, large = false }) => {
  const [pdfPosterUrl, setPdfPosterUrl] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  useEffect(() => {
    if (!resource?.url || !isPdf(resource)) return;
    let active = true;
    let objectUrl = '';
    setPdfLoading(true);
    void fetch(resource.url)
      .then((response) => response.arrayBuffer())
      .then(extractPdfPoster)
      .then((blob) => {
        if (!blob || !active) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfPosterUrl(objectUrl);
      })
      .catch(() => { if (active) setPdfPosterUrl(''); })
      .finally(() => { if (active) setPdfLoading(false); });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [resource]);

  if (!resource?.url) return <span aria-hidden="true">{variant === 'artifact' ? '▤' : '◎'}</span>;
  if (resource.kind === 'shot-batch') return large ? <ShotBatchExample url={resource.url} /> : <span style={{ fontSize: 14 }}>▶ Shot batch</span>;
  if (isPdf(resource) && pdfPosterUrl) return <img src={pdfPosterUrl} alt={`${resource.title} first page`} />;
  if (isPdf(resource) && pdfLoading) return <span className="cd-pdf-loading">Rendering…</span>;
  if (large && isVideo(resource)) return <video src={resource.url} controls />;
  if (large && isAudio(resource)) return <audio src={resource.url} controls preload="metadata" />;
  const videoEmbed = videoEmbedUrl(resource.url);
  if (large && videoEmbed) return <iframe src={videoEmbed} title={resource.description || resource.title} allow="autoplay; encrypted-media" allowFullScreen />;
  if (large && isPdf(resource)) return <a href={resource.url} target="_blank" rel="noreferrer">Open PDF separately ↗</a>;
  if (isImage(resource) || pdfPosterUrl) return <img src={pdfPosterUrl || resource.url} alt="" />;
  if (isAudio(resource)) return <span aria-label="Audio file" role="img">♪</span>;
  if (isVideo(resource) || videoEmbed) return <span className="cd-example-play-icon" aria-hidden="true">▶</span>;
  if (large && /^https?:\/\//i.test(resource.url)) return <div className="cd-example-live-embed">
    <iframe src={resource.url} title={resource.description || resource.title} allow="fullscreen; autoplay; clipboard-read; clipboard-write" sandbox="allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-presentation allow-scripts allow-same-origin" allowFullScreen />
    <div><small>If this site blocks embedding, open it separately.</small><a href={resource.url} target="_blank" rel="noreferrer">Open separately ↗</a></div>
  </div>;
  if (large) return <a href={resource.url} target="_blank" rel="noreferrer">Open {resource.title} ↗</a>;
  return <LinkIndicator />;
};

export const LessonOutcomesDisplay: React.FC<{ level: LevelLite }> = ({ level }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  useEffect(() => {
    if (!previewOpen) return;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') setPreviewOpen(false); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [previewOpen]);
  return <>
    {level.slideDeckUrl && <div className="cd-class-slide-action">
      <a className="cd-btn cd-btn-primary cd-slides-link" href={level.slideDeckUrl} target="_blank" rel="noreferrer">
        Open Slides ↗
      </a>
    </div>}
    <div className="cd-class-examples">
      <div className="cd-example-heading"><div className="cd-detail-label">Student artifact → AI output</div><button type="button" onClick={() => setPreviewOpen(true)} aria-label="Enlarge student artifact and AI output">⛶ Enlarge</button></div>
      <div className="cd-example-flow">
        <a className="cd-example-item" href={level.studentArtifactExample?.url || undefined} target={level.studentArtifactExample?.url ? '_blank' : undefined} rel="noreferrer"><span className="cd-example-thumb is-artifact"><ExampleMedia resource={level.studentArtifactExample} variant="artifact" /></span><small>{exampleLabel(level.studentArtifactExample, 'Student artifact')}</small></a>
        <span className="cd-example-arrow" aria-hidden="true">→</span>
        <a className="cd-example-item is-ai" onClick={event => { if (level.aiGeneratedExample?.kind === 'shot-batch') { event.preventDefault(); setPreviewOpen(true); } }} href={level.aiGeneratedExample?.url || undefined} target={level.aiGeneratedExample?.url ? '_blank' : undefined} rel="noreferrer"><span className="cd-example-thumb is-ai"><ExampleMedia resource={level.aiGeneratedExample} variant="ai" /></span><small>{exampleLabel(level.aiGeneratedExample, 'AI output')}</small></a>
      </div>
    </div>
    <div className="cd-class-outcomes"><div className="cd-refrain"><span aria-hidden="true">📣</span><strong>“{level.refrain || 'Refrain coming soon'}”</strong></div><p><strong>I can…</strong> {level.iCanStatement || 'Learning goal coming soon.'}</p></div>
    {previewOpen && <div className="cd-example-lightbox" role="dialog" aria-modal="true" aria-label="Student artifact and AI output" onMouseDown={(event) => { if (event.target === event.currentTarget) setPreviewOpen(false); }}><div className="cd-example-lightbox-card"><header><div><span>Student artifact → AI output</span><strong>{level.title}</strong></div><button type="button" onClick={() => setPreviewOpen(false)} aria-label="Close preview">×</button></header><div className="cd-example-lightbox-flow"><section><span>Student artifact</span><div className="cd-example-large-media is-artifact"><ExampleMedia resource={level.studentArtifactExample} variant="artifact" large /></div><strong>{exampleLabel(level.studentArtifactExample, 'Student artifact')}</strong></section><span className="cd-example-large-arrow" aria-hidden="true">→</span><section><span>AI output</span><div className="cd-example-large-media is-ai"><ExampleMedia resource={level.aiGeneratedExample} variant="ai" large /></div><strong>{exampleLabel(level.aiGeneratedExample, 'AI output')}</strong></section></div></div></div>}
  </>;
};

const ClassesView: React.FC<ClassesViewProps> = ({
  plan,
  levels,
  entries,
}) => {
  const [expandedSlotIndex, setExpandedSlotIndex] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'table'>('table');
  const [tableEnlarged, setTableEnlarged] = useState(false);
  const [tablePreview, setTablePreview] = useState<{ resource: CurriculumResource; variant: 'artifact' | 'ai'; levelTitle: string } | null>(null);

  useEffect(() => {
    if (!tableEnlarged) return;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setTableEnlarged(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [tableEnlarged]);

  useEffect(() => {
    if (viewMode !== 'table') setTableEnlarged(false);
  }, [viewMode]);

  useEffect(() => {
    if (!tablePreview) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setTablePreview(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [tablePreview]);

  return (
    <div className="cd-panel">
      {plan.mismatch ? (
        <div className="cd-banner cd-banner-warn">{plan.mismatch}</div>
      ) : null}
      <div className="cd-classes-view-toolbar">
        <span>Lesson overview</span>
        <div className="cd-classes-view-actions">
          <div className="cd-view-toggle" role="group" aria-label="Lessons view">
            <button type="button" aria-pressed={viewMode === 'list'} onClick={() => setViewMode('list')}>☷ Rows</button>
            <button type="button" aria-pressed={viewMode === 'table'} onClick={() => setViewMode('table')}>▦ Table</button>
          </div>
          {viewMode === 'table' && <button className="cd-table-enlarge-button" type="button" onClick={() => setTableEnlarged(true)} aria-label="Enlarge lesson table">⛶ Enlarge</button>}
        </div>
      </div>
      {viewMode === 'table' ? <div className={`cd-classes-table-wrap${tableEnlarged ? ' is-enlarged' : ''}`} role={tableEnlarged ? 'dialog' : undefined} aria-modal={tableEnlarged ? 'true' : undefined} aria-label={tableEnlarged ? 'Enlarged lesson overview table' : undefined}>
        {tableEnlarged && <div className="cd-classes-table-enlarged-header"><strong>Lesson overview</strong><button type="button" onClick={() => setTableEnlarged(false)} aria-label="Close enlarged lesson table">× Close</button></div>}
        <table className="cd-classes-table">
        <thead><tr><th>Lesson</th><th>Title</th><th>Slides</th><th>Student artifact</th><th>AI output</th><th>Thinking theme</th><th>Refrain</th><th>I can…</th><th>Status</th></tr></thead>
        <tbody>{plan.slots.map((slot) => {
          const level = levels.find((item) => item.id === slot.levelId);
          const summary = summarizeEntriesForLevel(entries, slot.levelId);
          const statusLabel = slot.status === 'completed' && summary.aiRevealed > 0 ? '✦ Revealed' : slot.status === 'live' ? 'Live' : slot.status === 'next' ? 'Next' : 'Preview';
          const statusClass = slot.status === 'completed' && summary.aiRevealed > 0 ? 'cd-pill-reveal' : slot.status === 'live' ? 'cd-pill-live' : slot.status === 'next' ? 'cd-pill-next' : 'cd-pill-muted';
          return <tr key={`${slot.index}-${slot.levelId}`} className={slot.status === 'live' ? 'is-live' : slot.status === 'future' ? 'is-future' : ''}>
            <td><strong>{String(slot.index).padStart(2, '0')}</strong><small>{slot.date ? formatSlotDate(slot.date) : 'Date TBD'}</small></td>
            <td><strong>{slot.levelTitle}</strong></td>
            <td>{level?.slideDeckUrl ? <a className="cd-table-slides-link" href={level.slideDeckUrl} target="_blank" rel="noreferrer">Open Slides ↗</a> : <span className="cd-table-empty">—</span>}</td>
            <td>{level?.studentArtifactExample ? <button className="cd-table-example" type="button" onClick={() => setTablePreview({ resource: level.studentArtifactExample!, variant: 'artifact', levelTitle: level.title })} aria-label={`Enlarge ${exampleLabel(level.studentArtifactExample, 'student artifact')}`}><span className="cd-example-thumb is-artifact"><ExampleMedia resource={level.studentArtifactExample} variant="artifact" /></span><small>{exampleLabel(level.studentArtifactExample, 'Artifact')}</small></button> : <span className="cd-table-empty">—</span>}</td>
            <td>{level?.aiGeneratedExample ? <button className="cd-table-example" type="button" onClick={() => setTablePreview({ resource: level.aiGeneratedExample!, variant: 'ai', levelTitle: level.title })} aria-label={`Enlarge ${exampleLabel(level.aiGeneratedExample, 'AI output')}`}><span className="cd-example-thumb is-ai"><ExampleMedia resource={level.aiGeneratedExample} variant="ai" /></span><small>{exampleLabel(level.aiGeneratedExample, 'AI output')}</small></button> : <span className="cd-table-empty">—</span>}</td>
            <td>{level?.thinkingSkill ? <span className="cd-pill cd-pill-theme">{level.thinkingSkill}</span> : <span className="cd-table-empty">—</span>}</td>
            <td className="cd-table-refrain">{level?.refrain ? `“${level.refrain}”` : <span className="cd-table-empty">—</span>}</td>
            <td>{level?.iCanStatement || <span className="cd-table-empty">—</span>}</td>
            <td><span className={`cd-pill ${statusClass}`}>{statusLabel}</span></td>
          </tr>;
        })}</tbody>
      </table></div> : <div className="cd-strip">
        {plan.slots.map((slot) => {
          const frameClass =
            slot.status === 'live'
              ? 'is-live'
              : slot.status === 'next'
                ? 'is-next'
                : slot.status === 'future'
                  ? 'is-future'
                  : '';

          const summary = summarizeEntriesForLevel(entries, slot.levelId);
          const level = levels.find((item) => item.id === slot.levelId);
          const developed = Boolean(level && !/^\s*(class\s+\d+:\s*)?to be developed\s*$/i.test(level.title));
          const expanded = developed && expandedSlotIndex === slot.index;
          const statusLabel = slot.status === 'completed' && summary.aiRevealed > 0
            ? '✦ Revealed'
            : slot.status === 'live'
              ? 'Live'
              : slot.status === 'next'
                ? 'Next'
                : 'Preview';

          return (
            <article
              key={`${slot.index}-${slot.levelId}`}
              className={`cd-frame ${frameClass}${expanded ? ' is-expanded' : ''}${developed ? ' is-expandable' : ''}`}
            >
              <button
                type="button"
                className="cd-frame-summary"
                aria-expanded={developed ? expanded : undefined}
                onClick={() => developed && setExpandedSlotIndex(expanded ? null : slot.index)}
              >
                <span className="cd-frame-num">{String(slot.index).padStart(2, '0')}</span>
                <div className="cd-frame-mid">
                  <div className="cd-frame-name">{slot.levelTitle}</div>
                  <div className="cd-frame-date">
                    {slot.date ? formatSlotDate(slot.date) : 'Date TBD'}
                  </div>
                </div>
                <div className="cd-frame-chips">
                  {level?.thinkingSkill && <span className="cd-pill cd-pill-theme">{level.thinkingSkill}</span>}
                  <span className={`cd-pill ${slot.status === 'completed' && summary.aiRevealed > 0 ? 'cd-pill-reveal' : slot.status === 'live' ? 'cd-pill-live' : slot.status === 'next' ? 'cd-pill-next' : 'cd-pill-muted'}`}>{statusLabel}</span>
                </div>
                {developed && <span className="cd-frame-chevron" aria-hidden="true">⌄</span>}
              </button>
              {expanded && level && <div className="cd-frame-details"><LessonOutcomesDisplay level={level} /></div>}
            </article>
          );
        })}
      </div>}
      {tablePreview && <div className="cd-example-lightbox" role="dialog" aria-modal="true" aria-label={`Enlarged ${exampleLabel(tablePreview.resource, 'example')}`} onMouseDown={(event) => { if (event.target === event.currentTarget) setTablePreview(null); }}><div className="cd-example-lightbox-card cd-example-lightbox-single"><header><div><span>{tablePreview.variant === 'artifact' ? 'Student artifact' : 'AI output'}</span><strong>{tablePreview.levelTitle}</strong></div><button type="button" onClick={() => setTablePreview(null)} aria-label="Close enlarged example">×</button></header><div className={`cd-example-large-media ${tablePreview.variant === 'ai' ? 'is-ai' : 'is-artifact'}`}><ExampleMedia resource={tablePreview.resource} variant={tablePreview.variant} large /></div><footer><strong>{exampleLabel(tablePreview.resource, tablePreview.variant === 'artifact' ? 'Student artifact' : 'AI output')}</strong>{tablePreview.resource.url && tablePreview.resource.kind !== 'shot-batch' && <a href={tablePreview.resource.url} target="_blank" rel="noreferrer">Open separately ↗</a>}</footer></div></div>}
      <p className="cd-caption">
        {viewMode === 'list' ? 'Tap a developed lesson to preview its student artifact, AI output, and learning outcomes.' : 'Scroll horizontally to compare every part of the curriculum.'}
      </p>
    </div>
  );
};

export default ClassesView;
